import React, { useState, useEffect } from 'react';
import { Layout, Menu, Typography, Space, Avatar, Button, Input, Select, Table, Pagination, Tabs, Tag, Modal, message, Spin, Empty, Tooltip } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminSidebar from './AdminSidebar';
import UserProfile from './UserProfile';
import DashboardSection from './DashboardSection';
import PendingApprovalsHighlights from './PendingApprovalsHighlights';
import CharityAccessRequests from './CharityAccessRequests';
import { approvalsAPI, beneficiaryAPI, vendorAPI } from '../services/api';
import {
  DashboardOutlined, UserOutlined, SettingOutlined,
  CrownOutlined, ExclamationCircleOutlined,
  MenuOutlined, MoreOutlined, SearchOutlined,
  SortAscendingOutlined, CheckCircleOutlined, CloseCircleOutlined,
  EyeOutlined, ShopOutlined, HeartOutlined, TeamOutlined, GlobalOutlined,
  CalculatorOutlined, MailOutlined, TrophyOutlined
} from '@ant-design/icons';
import './PendingApprovals.css';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

// Unified interface for both vendor and beneficiary data
interface ApprovalItem {
  key: string;
  name: string;
  contactPerson: string;
  email: string;
  phone: string;
  type: string;
  location: string;
  registrationDate: string;
  createdAt: number | null;
  documentsSubmitted: string;
  verificationStatus: string;
  active: boolean;
  enabled: boolean;
  avatar: string;
  itemType: 'vendor' | 'beneficiary';
  /** Registered but never submitted for review — nothing to judge yet. */
  isIncomplete?: boolean;
}

const PendingApprovals: React.FC = () => {
  const [mobileSidebarVisible, setMobileSidebarVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  // An approval queue should show everything waiting, including submissions
  // older than a month — those are the ones most in need of attention. The
  // 30-day default was hiding them while still counting them in the tab badge.
  const [selectedTimeFilter, setSelectedTimeFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('vendors');
  const [approvalModalVisible, setApprovalModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ApprovalItem | null>(null);
  // Profile detail an admin fills in while approving a donor-suggested
  // charity. Sent with the approve call so the cause lands in the app with a
  // real profile instead of the placeholder it carried while pending.
  const [charityProfile, setCharityProfile] = useState<Record<string, string>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('all');
  const [selectedDocumentFilter, setSelectedDocumentFilter] = useState('all');
  const [approvalsData, setApprovalsData] = useState<ApprovalItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalApprovals, setTotalApprovals] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  // Load pending approvals from API
  // Gracefully handles missing endpoint / empty data - shows empty state instead of errors
  const loadApprovals = async () => {
    setLoading(true);
    
    try {
      // Which queue to fetch. Without this the Beneficiaries tab silently
      // showed the vendor list filtered to nothing.
      const response = await approvalsAPI.getPendingApprovals(
        currentPage,
        pageSize,
        activeTab === 'vendors' ? 'vendor' : 'beneficiary',
      );
      
      if (response?.success && Array.isArray(response.data)) {
        const transformedData = response.data.map((approval: any) => ({
          key: approval.id.toString(),
          name: approval.name || 'Unknown',
          contactPerson: approval.contact_person || 'N/A',
          email: approval.email || 'N/A',
          phone: approval.phone || 'N/A',
          type: approval.type || 'vendor',
          location: approval.location || 'N/A',
          // submitted_at is when the item entered the queue; created_at is when
          // the underlying row was first created. For a charity those differ —
          // /charities/suggest reuses an existing row when the EIN matches — so
          // filtering on created_at hid recent submissions behind the 30-day
          // filter while still counting them in the tab badge.
          registrationDate: (approval.submitted_at || approval.created_at)
            ? new Date(approval.submitted_at || approval.created_at).toLocaleDateString()
            : 'N/A',
          createdAt: (approval.submitted_at || approval.created_at)
            ? new Date(approval.submitted_at || approval.created_at).getTime()
            : null,
          documentsSubmitted: approval.documents_submitted || 'N/A',
          verificationStatus: approval.verification_status || 'pending',
          active: approval.is_active || false,
          enabled: approval.is_enabled || false,
          avatar: approval.name ? approval.name.charAt(0).toUpperCase() : 'A',
          itemType: (approval.type === 'beneficiary' ? 'beneficiary' : 'vendor') as 'vendor' | 'beneficiary',
          isIncomplete:
            approval.is_incomplete === true || approval.submission_state === 'incomplete',
        }));
        
        setApprovalsData(transformedData);
        setTotalApprovals(response.pagination?.total ?? transformedData.length);
      } else {
        // No error state - treat as empty (endpoint may not exist yet)
        setApprovalsData([]);
        setTotalApprovals(0);
      }
    } catch {
      // Gracefully handle API errors - show empty state, no error message
      setApprovalsData([]);
      setTotalApprovals(0);
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount and when page changes
  useEffect(() => {
    loadApprovals();
  }, [currentPage, pageSize, activeTab]);

  const handleToggleChange = async (key: string, field: 'active' | 'enabled') => {
    const item = approvalsData.find((i) => i.key === key);
    if (!item) return;
    const id = parseInt(key, 10);
    if (isNaN(id)) return;
    const nextVal = !item[field];
    try {
      if (item.itemType === 'beneficiary') {
        const payload = field === 'active'
          ? { is_active: nextVal, isActive: nextVal }
          : { is_enabled: nextVal, isEnabled: nextVal };
        const response = await beneficiaryAPI.updateBeneficiary(id, payload);
        if (response?.success !== false) {
          setApprovalsData(prev =>
            prev.map(i => (i.key === key ? { ...i, [field]: nextVal } : i))
          );
          message.success('Status updated');
          loadApprovals(); // Refresh from server
        } else {
          message.error('Failed to update');
        }
      } else {
        if (field === 'active') {
          const nextStatus = nextVal ? 'active' : 'inactive';
          const response = await vendorAPI.updateVendorStatus(id, nextStatus);
          if (response?.success !== false) {
            setApprovalsData(prev =>
              prev.map(i =>
                i.key === key
                  ? { ...i, active: nextVal, enabled: nextVal }
                  : i
              )
            );
            message.success('Status updated');
            loadApprovals(); // Refresh from server
          } else {
            message.error('Failed to update');
          }
        } else {
          const response = await vendorAPI.updateVendor(id, { is_enabled: nextVal });
          if (response?.success !== false) {
            setApprovalsData(prev =>
              prev.map(i => (i.key === key ? { ...i, enabled: nextVal } : i))
            );
            message.success('Status updated');
            loadApprovals(); // Refresh from server
          } else {
            message.error('Failed to update');
          }
        }
      }
    } catch (err: any) {
      console.error('Toggle error:', err);
      message.error(err?.message || 'Failed to update');
    }
  };

  const handleTimeFilterChange = (key: string) => {
    setSelectedTimeFilter(key);
  };

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const clearSearch = () => {
    setSearchQuery('');
    setCurrentPage(1);
  };

  const timeFilterMenu = [
    {
      key: '7-days',
      label: 'Last 7 Days',
      onClick: () => handleTimeFilterChange('7-days')
    },
    {
      key: '30-days',
      label: 'Last 30 Days',
      onClick: () => handleTimeFilterChange('30-days')
    },
    {
      key: '90-days',
      label: 'Last 90 Days',
      onClick: () => handleTimeFilterChange('90-days')
    },
    {
      key: '1-year',
      label: 'Last 1 Year',
      onClick: () => handleTimeFilterChange('1-year')
    },
    {
      key: 'all-time',
      label: 'All Time',
      onClick: () => handleTimeFilterChange('all-time')
    }
  ];

  const vendorCount = approvalsData.filter((item) => item.itemType === 'vendor').length;
  const beneficiaryCount = approvalsData.filter((item) => item.itemType === 'beneficiary').length;

  // Unified columns that work for both vendor and beneficiary data
  const approvalColumns = [
    {
      title: (
        <div className="sortable-header">
          {activeTab === 'vendors' ? 'Company Name' : 'Organization Name'}
          <SortAscendingOutlined className="sort-icon" />
        </div>
      ),
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: ApprovalItem) => (
        <Space>
          <Avatar size={32} style={{ backgroundColor: '#DB8633' }}>
            {record.avatar}
          </Avatar>
          <Text strong>{text}</Text>
          {/* Registered through the portal but never submitted for review.
              There is nothing to approve yet — these need chasing, not a
              decision, so the tag says so rather than looking like a normal
              pending application. */}
          {record.isIncomplete && (
            <Tooltip title="Signed up but hasn't submitted their profile for review yet — follow up with them.">
              <Tag color="orange">Incomplete</Tag>
            </Tooltip>
          )}
        </Space>
      ),
      fixed: 'left' as const,
      width: 260,
    },
    {
      title: activeTab === 'vendors' ? 'Owner Name' : 'Contact Person',
      dataIndex: 'contactPerson',
      key: 'contactPerson',
      render: (text: string) => <Text type="secondary">{text}</Text>,
      width: 150,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (text: string) => <Text type="secondary">{text}</Text>,
      width: 200,
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      render: (text: string) => <Text type="secondary">{text}</Text>,
      width: 140,
    },
    {
      title: activeTab === 'vendors' ? 'Business Type' : 'Organization Type',
      dataIndex: 'type',
      key: 'type',
      render: (text: string) => <Text type="secondary">{text}</Text>,
      width: 150,
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      render: (text: string) => <Text type="secondary">{text}</Text>,
      width: 140,
    },
    {
      title: 'Registration Date',
      dataIndex: 'registrationDate',
      key: 'registrationDate',
      render: (text: string) => <Text type="secondary">{text}</Text>,
      width: 150,
    },
    {
      title: 'Documents',
      dataIndex: 'documentsSubmitted',
      key: 'documentsSubmitted',
      render: (text: string) => (
        <Tag color={text === 'Complete' ? 'green' : 'orange'}>
          {text}
        </Tag>
      ),
      width: 120,
    },
    {
      title: 'Verification',
      dataIndex: 'verificationStatus',
      key: 'verificationStatus',
      render: (text: string) => (
        <Tag color="blue">{text}</Tag>
      ),
      width: 120,
    },
    {
      title: 'Active/De-active',
      dataIndex: 'active',
      key: 'active',
      render: (active: boolean, record: ApprovalItem) => (
        <div className="toggle-switch">
          <div className={`toggle ${active ? 'active' : 'inactive'}`} onClick={() => handleToggleChange(record.key, 'active')}>
            <div className="toggle-slider"></div>
          </div>
        </div>
      ),
      width: 160,
    },
    {
      title: 'Enable/Disable',
      dataIndex: 'enabled',
      key: 'enabled',
      render: (enabled: boolean, record: ApprovalItem) => (
        <div className="toggle-switch">
          <div className={`toggle ${enabled ? 'active' : 'inactive'}`} onClick={() => handleToggleChange(record.key, 'enabled')}>
            <div className="toggle-slider"></div>
          </div>
        </div>
      ),
      width: 160,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (text: string, record: ApprovalItem) => (
        <Space>
          <Button 
            type="text" 
            icon={<EyeOutlined />} 
            size="small" 
            className="view-action-btn"
            onClick={() => handleViewDetails(record)}
          />
          <Button 
            type="text" 
            icon={<CheckCircleOutlined />} 
            size="small" 
            className="approve-action-btn"
            onClick={() => handleApprove(record)}
          />
          <Button 
            type="text" 
            icon={<CloseCircleOutlined />} 
            size="small" 
            className="reject-action-btn"
            onClick={() => handleReject(record)}
          />
        </Space>
      ),
      width: 150,
      fixed: 'right' as const,
    },
  ];

  const handlePageChange = (page: number, size?: number) => {
    setCurrentPage(page);
    if (size) setPageSize(size);
  };

  const handleViewDetails = (record: ApprovalItem) => {
    setSelectedItem(record);
    setApprovalModalVisible(true);
  };

  const handleApprove = async (record: ApprovalItem) => {
    try {
      // Only non-empty fields, so a blank input can't overwrite detail the
      // charity already has.
      const profile = Object.fromEntries(
        Object.entries(charityProfile).filter(([, v]) => String(v || '').trim() !== ''),
      );
      const response = await approvalsAPI.approveItem(
        parseInt(record.key),
        record.itemType,
        record.itemType === 'beneficiary' ? profile : undefined,
      );
      
      if (response.success) {
        const r: any = response;
        const missing: string[] = Array.isArray(r.missingFields) ? r.missingFields : [];

        if (record.itemType !== 'vendor' && r.isComplete === false) {
          // Approved, but held back from donors until the profile is filled
          // in. Hand straight off to the profile editor rather than dropping
          // the admin back on a queue with no hint of what to do next —
          // saving that profile is what publishes the charity.
          message.success({
            content: `${record.name} approved. Complete the profile to make it visible to donors${
              missing.length ? ` — still needed: ${missing.join(', ')}` : ''
            }.`,
            duration: 8,
          });
          setApprovalModalVisible(false);
          navigate('/beneficiaries', {
            state: {
              openBeneficiaryId: record.key,
              completeProfileFor: record.name,
              missingFields: missing,
            },
          });
          return;
        }

        message.success(`${record.itemType === 'vendor' ? 'Vendor' : 'Beneficiary'} approved successfully!`);
        // Refresh the approvals list
        loadApprovals();
      } else {
        message.error('Failed to approve item');
      }
    } catch (error) {
      console.error('Error approving item:', error);
      message.error('Failed to approve item. Please try again.');
    }
  };

  const handleReject = async (record: ApprovalItem) => {
    try {
      const response = await approvalsAPI.rejectItem(parseInt(record.key), record.itemType, 'Rejected by admin');
      
      if (response.success) {
        message.error(`${record.itemType === 'vendor' ? 'Vendor' : 'Beneficiary'} rejected.`);
        // Refresh the approvals list
        loadApprovals();
      } else {
        message.error('Failed to reject item');
      }
    } catch (error) {
      console.error('Error rejecting item:', error);
      message.error('Failed to reject item. Please try again.');
    }
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setCurrentPage(1);
  };

  const getCurrentData = (): ApprovalItem[] => {
    const baseData = approvalsData.filter((item) =>
      activeTab === 'vendors' ? item.itemType === 'vendor' : item.itemType === 'beneficiary'
    );

    const filteredByStatus =
      selectedStatusFilter === 'all'
        ? baseData
        : baseData.filter((item) => item.verificationStatus?.toLowerCase() === selectedStatusFilter);

    const filteredByDocuments =
      selectedDocumentFilter === 'all'
        ? filteredByStatus
        : filteredByStatus.filter((item) => {
            const docStatus = item.documentsSubmitted?.toLowerCase();
            if (selectedDocumentFilter === 'complete') return docStatus === 'complete';
            if (selectedDocumentFilter === 'incomplete') return docStatus === 'incomplete';
            if (selectedDocumentFilter === 'pending-review') return docStatus === 'pending review' || docStatus === 'pending-review';
            return true;
          });

    const filteredByTime =
      selectedTimeFilter === 'all'
        ? filteredByDocuments
        : filteredByDocuments.filter((item) => {
            if (!item.createdAt) return true;
            const diffDays = (Date.now() - item.createdAt) / (1000 * 60 * 60 * 24);
            if (selectedTimeFilter === '7-days') return diffDays <= 7;
            if (selectedTimeFilter === '30-days') return diffDays <= 30;
            if (selectedTimeFilter === '90-days') return diffDays <= 90;
            if (selectedTimeFilter === '1-year') return diffDays <= 365;
            return true;
          });

    if (!searchQuery.trim()) {
      return filteredByTime;
    }

    return filteredByTime.filter(item =>
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.contactPerson.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.phone.includes(searchQuery) ||
      item.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.location.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const getTotalCount = (): number => {
    return getCurrentData().length;
  };

  const highlightSearchText = (text: string) => {
    if (!searchQuery.trim()) return text;
    
    const regex = new RegExp(`(${searchQuery})`, 'gi');
    return text.replace(regex, '<mark style="background-color: #FFE4B5; padding: 2px 4px; border-radius: 3px;">$1</mark>');
  };

  return (
    <Layout className="donors-layout">
      <AdminSidebar
        activeKey="pending-approvals"
        mobileVisible={mobileSidebarVisible}
        onMobileToggle={() => setMobileSidebarVisible(!mobileSidebarVisible)}
      />

      {/* Main Content */}
      <Layout className="main-content">
        <Header className="donors-header">
          <div className="header-left">
            <Title level={2} style={{ margin: 0 }}>Pending Approvals</Title>
            <Text type="secondary" className="approvals-count">
              {getTotalCount()} {activeTab === 'vendors' ? 'Vendors' : 'Beneficiaries'} Pending Approval
            </Text>
          </div>
          <div className="header-right">
            <Button 
              type="primary" 
              icon={<ExclamationCircleOutlined />}
              size="large"
              className="approval-stats-btn"
            >
              Approval Statistics
            </Button>
          </div>
        </Header>

        <Content className="donors-content">
          <div className="content-wrapper">
            {/* Renders nothing unless someone is waiting, so it stays out of
                the way until there is a decision to make. Sits above the tabs
                because it answers a different question from them: who may
                edit a charity, not whether a charity should be visible. */}
            <CharityAccessRequests />

            <DashboardSection
              title="Approval Queue Highlights"
              subtitle="What's waiting on your review"
              icon={<TrophyOutlined />}
            >
              <PendingApprovalsHighlights approvals={approvalsData} />
            </DashboardSection>

            <DashboardSection
              title="Pending Approvals"
              subtitle="Verify and approve new beneficiaries and vendors"
              icon={<ExclamationCircleOutlined />}
            >
            {/* Tabs for Vendors vs Beneficiaries */}
            <div className="approval-tabs-section">
              <Tabs 
                activeKey={activeTab} 
                onChange={handleTabChange}
                className="approval-tabs"
                items={[
                  {
                    key: 'vendors',
                    label: (
                      <span>
                        <ShopOutlined />
                        Vendors ({vendorCount})
                      </span>
                    ),
                  },
                  {
                    key: 'beneficiaries',
                    label: (
                      <span>
                        <HeartOutlined />
                        Beneficiaries ({beneficiaryCount})
                      </span>
                    ),
                  },
                ]}
              />
            </div>

            {/* Search and Filter Bar */}
            <div className="search-filter-bar">
              <div className="search-section">
                <Search
                  placeholder={`Search ${activeTab === 'vendors' ? 'Vendors' : 'Beneficiaries'}`}
                  allowClear
                  enterButton={<SearchOutlined />}
                  size="large"
                  className="approval-search"
                  value={searchQuery}
                  onSearch={handleSearch}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              
              <div className="filter-section">
                <Text strong className="filter-label">Filters</Text>
                <div className="filter-dropdowns">
                  <Select
                    placeholder="Select Status"
                    className="filter-dropdown"
                    size="large"
                    value={selectedStatusFilter}
                    onChange={setSelectedStatusFilter}
                  >
                    <Option value="all">All Status</Option>
                    <Option value="pending">Pending</Option>
                    <Option value="approved">Approved</Option>
                    <Option value="rejected">Rejected</Option>
                  </Select>
                  
                  <Select
                    placeholder="Documents Status"
                    className="filter-dropdown"
                    size="large"
                    value={selectedDocumentFilter}
                    onChange={setSelectedDocumentFilter}
                  >
                    <Option value="all">All Documents</Option>
                    <Option value="complete">Complete</Option>
                    <Option value="incomplete">Incomplete</Option>
                    <Option value="pending-review">Pending Review</Option>
                  </Select>
                  
                  <Select
                    placeholder="Time Period"
                    className="filter-dropdown"
                    size="large"
                    value={selectedTimeFilter}
                    onChange={setSelectedTimeFilter}
                  >
                    <Option value="7-days">Last 7 Days</Option>
                    <Option value="30-days">Last 30 Days</Option>
                    <Option value="90-days">Last 90 Days</Option>
                    <Option value="all">All Time</Option>
                  </Select>
                </div>
              </div>
            </div>

            {/* Approvals Table */}
            <div className="approvals-table-section">
              <Spin spinning={loading}>
                <Table
                  dataSource={getCurrentData()}
                  columns={approvalColumns}
                  pagination={false}
                  size="middle"
                  className="approvals-table"
                rowClassName="approval-row"
                scroll={{ x: 1800 }}
                bordered={false}
                locale={{
                  emptyText: (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={
                        <span>
                          No pending {activeTab === 'vendors' ? 'vendors' : 'beneficiaries'} yet.
                          <br />
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            When vendors and beneficiaries have their own admin panels and create accounts, they will appear here for approval.
                          </Text>
                        </span>
                      }
                    />
                  )
                }}
              />
              </Spin>
              
              {/* Pagination */}
              <div className="pagination-section">
                <Pagination
                  current={currentPage}
                  total={totalApprovals}
                  pageSize={pageSize}
                  showSizeChanger={false}
                  showQuickJumper={false}
                  onChange={handlePageChange}
                  className="approvals-pagination"
                />
              </div>
            </div>
            </DashboardSection>
          </div>
        </Content>
      </Layout>

      {/* Approval Details Modal */}
      <Modal
        title={
          <div className="modal-header">
            <ExclamationCircleOutlined className="modal-icon" />
            <Title level={3} style={{ margin: 0, color: '#DB8633' }}>
              {selectedItem?.name} - Approval Details
            </Title>
          </div>
        }
        open={approvalModalVisible}
        onCancel={() => {
          setApprovalModalVisible(false);
          setCharityProfile({});
        }}
        footer={[
          <Button key="reject" danger onClick={() => {
            if (selectedItem) {
              handleReject(selectedItem);
              setApprovalModalVisible(false);
            }
          }}>
            Reject
          </Button>,
          <Button key="approve" type="primary" onClick={() => {
            if (selectedItem) {
              handleApprove(selectedItem);
              setApprovalModalVisible(false);
              setCharityProfile({});
            }
          }}>
            Approve
          </Button>,
        ]}
        width={800}
        className="approval-details-modal"
      >
        {selectedItem && (
          <div className="approval-details">
        {selectedItem?.itemType === 'beneficiary' && (
          /* Donor-suggested charities arrive with only what the IRS registry
             gave us — name, EIN, city. Approving without filling this in
             leaves the cause live with placeholder art and no story, so the
             form sits above the read-only details. Blank fields are dropped,
             never written as empty. */
          <div className="detail-section">
            <Title level={4}>Complete the charity profile</Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              Filled in when you approve. Anything left blank keeps its current value.
            </Text>
            <Space direction="vertical" style={{ width: '100%' }} size={10}>
              <Input
                placeholder="Category (e.g. Hunger Relief)"
                value={charityProfile.category || ''}
                onChange={(e) => setCharityProfile((p) => ({ ...p, category: e.target.value }))}
              />
              <Input
                placeholder="Short description — one line shown on the card"
                value={charityProfile.description || ''}
                onChange={(e) => setCharityProfile((p) => ({ ...p, description: e.target.value }))}
              />
              <Input.TextArea
                rows={3}
                placeholder="About — who they are and what they do"
                value={charityProfile.about || ''}
                onChange={(e) => setCharityProfile((p) => ({ ...p, about: e.target.value }))}
              />
              <Input
                placeholder="Hero image URL"
                value={charityProfile.imageUrl || ''}
                onChange={(e) => setCharityProfile((p) => ({ ...p, imageUrl: e.target.value }))}
              />
              <Input
                placeholder="Logo URL"
                value={charityProfile.logoUrl || ''}
                onChange={(e) => setCharityProfile((p) => ({ ...p, logoUrl: e.target.value }))}
              />
              <Input
                placeholder="Website"
                value={charityProfile.website || ''}
                onChange={(e) => setCharityProfile((p) => ({ ...p, website: e.target.value }))}
              />
              <Input
                placeholder="Contact name"
                value={charityProfile.contactName || ''}
                onChange={(e) => setCharityProfile((p) => ({ ...p, contactName: e.target.value }))}
              />
              <Input
                placeholder="Phone"
                value={charityProfile.phone || ''}
                onChange={(e) => setCharityProfile((p) => ({ ...p, phone: e.target.value }))}
              />
              <Input
                placeholder="Full address — used to place them on the donor map"
                value={charityProfile.location || ''}
                onChange={(e) => setCharityProfile((p) => ({ ...p, location: e.target.value }))}
              />
            </Space>
          </div>
        )}

            <div className="detail-section">
              <Title level={4}>Basic Information</Title>
              <div className="detail-grid">
                <div className="detail-item">
                  <Text strong>Name:</Text>
                  <Text>{selectedItem.name}</Text>
                </div>
                <div className="detail-item">
                  <Text strong>Contact:</Text>
                  <Text>{selectedItem.contactPerson}</Text>
                </div>
                <div className="detail-item">
                  <Text strong>Email:</Text>
                  <Text>{selectedItem.email}</Text>
                </div>
                <div className="detail-item">
                  <Text strong>Phone:</Text>
                  <Text>{selectedItem.phone}</Text>
                </div>
                <div className="detail-item">
                  <Text strong>Location:</Text>
                  <Text>{selectedItem.location}</Text>
                </div>
                <div className="detail-item">
                  <Text strong>Registration Date:</Text>
                  <Text>{selectedItem.registrationDate}</Text>
                </div>
              </div>
            </div>
            
            <div className="detail-section">
              <Title level={4}>Verification Status</Title>
              <div className="status-grid">
                <div className="status-item">
                  <Text strong>Documents:</Text>
                  <Tag color={selectedItem.documentsSubmitted === 'Complete' ? 'green' : 'orange'}>
                    {selectedItem.documentsSubmitted}
                  </Tag>
                </div>
                <div className="status-item">
                  <Text strong>Verification:</Text>
                  <Tag color="blue">{selectedItem.verificationStatus}</Tag>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <Title level={4}>Additional Notes</Title>
              <Text type="secondary">
                Review all submitted documents and verify {selectedItem.itemType === 'vendor' ? 'business legitimacy' : 'organization legitimacy'} before approval.
                Ensure compliance with platform guidelines and requirements.
              </Text>
            </div>
          </div>
        )}
      </Modal>
    </Layout>
  );
};

export default PendingApprovals; 